import { useMediaQuery } from "@mui/material"
import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { navigate } from "gatsby"
import { addItemToCart } from "../../checkout/events"
import { useCheckoutDispatch, useCheckoutState } from "../../checkout/state"
import theme from "../../material-ui/theme"
import { useAuthState } from "../../contexts/authContext"
import { setLs } from "../../utils/localStorage"
import LoadingSpinner from "../ui/LoadingSpinner"
import { StrapiMembership } from "../../hooks/useMembershipConfig"
import { findProduct, useProducts } from "../../hooks/useProducts"
import { useCurrentUser } from "../../hooks/useCurrentUser"
import MembershipSectionCard from "../cart/MembershipSectionCard"
import ProSignup from "../auth/ProSignup"

type PageState = "NOT_LOGGED_IN" | "LOGGED_IN" | "LOADING"

const Container = styled.div<{
  gridSize: number
}>`
  display: grid;
  grid-template-columns: ${props =>
    props.gridSize === 1
      ? "minmax(320px, 320px)"
      : props.gridSize === 2
      ? "minmax(320px, 320px) minmax(320px, 320px)"
      : "minmax(375px, 375px) minmax(375px, 375px) minmax(375px, 375px)"};
  gap: 27px;
  width: ${props => (props.gridSize <= 2 ? "48%" : "60%")};
  margin: 0 auto;
  align-items: stretch;
  justify-content: center;
`

const MembershipSection = (section: any) => {
  const checkoutState = useCheckoutState()

  const checkoutDispatch = useCheckoutDispatch()
  const authState = useAuthState()

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))
  const [pageStage, setPageState] = useState<PageState>("LOADING")
  const [personalMembershipLimit, setPersonalMembershipLimit] = useState<
    boolean
  >(false)
  const [proMembershipLimit, setProMembershipLimit] = useState<boolean>(false)

  const [showProSignup, setShowProSignup] = useState<boolean>(false)
  const [pendingPro, setPeningPro] = useState<StrapiMembership | undefined>(
    undefined
  )

  const { user, fetch } = useCurrentUser()
  const { isPromo } = section

  const products = useProducts()

  section?.membership.forEach((m: any) => {
    if (m.product) {
      m.product = products.find(p => p.id === m.product.id)
    }
  })

  const memberships = section?.membership
    ? (section.membership as StrapiMembership[])
    : undefined

  const handleBuySubscription = (membership: StrapiMembership) => {
    if (!membership.product) {
      return
    }

    if (membership.product.isPro && !user?.hasProConfig()) {
      setPeningPro(membership)
      setShowProSignup(true)
      return
    }
    if (membership.couponCode) {
      let expiry = new Date()

      expiry.setDate(expiry.getDate() + 10)

      setLs("coupon", {
        id: membership.couponCode,
        expiry: expiry.toISOString(),
      })
    }

    const cardProductBundled = membership.additionalProduct
      ? findProduct(products, membership.additionalProduct.id)
      : undefined

    addItemToCart(
      checkoutState,
      checkoutDispatch,
      1,
      cardProductBundled
        ? [membership.product, cardProductBundled] //the order is important
        : [membership.product],
      products
    )

    if (section.isModalOpen) {
      section.setIsModalOpen(false)
    } else {
      navigate("/store/checkout")
    }
  }

  useEffect(() => {
    if (authState.isLoggedIn() && !user) {
      setPageState("LOADING")
    } else if (user) {
      setPageState("LOGGED_IN")
      setPersonalMembershipLimit(user.hasPersonalMembership())
      setProMembershipLimit(user.hasProfessionalMembership())
    } else {
      setPageState("NOT_LOGGED_IN")
      setPersonalMembershipLimit(false)
      setProMembershipLimit(false)
    }
  }, [user, authState])

  useEffect(() => {
    if (user && user.hasProConfig() && pendingPro && showProSignup) {
      handleBuySubscription(pendingPro)
    }
  }, [user])

  const renderSelectMembership = () => (
    <>
      <ProSignup
        setModalOpen={setShowProSignup}
        modalOpen={showProSignup}
        onComplete={() => {
          //refetch the user
          fetch({})
        }}
      />
      <Container
        gridSize={
          isMobile || isPromo ? 1 : Math.min(3, memberships?.length ?? 1)
        }
      >
        {section &&
          memberships &&
          memberships
            .filter(m => !isPromo || m.product?.isYearly())
            .map((membership, i) => {
              return (
                <MembershipSectionCard
                  key={`${i}`}
                  membership={membership}
                  isPromo={isPromo}
                  disabled={pageStage === "LOADING"}
                  handleBuySubscription={handleBuySubscription}
                  personalMembershipLimit={personalMembershipLimit}
                  professionalMembershipLimit={proMembershipLimit}
                  hasProOrg={user?.hasProConfig() ?? false}
                />
              )
            })}
      </Container>
    </>
  )

  switch (pageStage) {
    case "LOADING":
      return (
        <>
          <ProSignup
            setModalOpen={setShowProSignup}
            modalOpen={showProSignup}
            onComplete={() => {
              //refetch the user
              fetch({})
            }}
          />
          <LoadingSpinner />
        </>
      )
    default:
      return renderSelectMembership()
  }
}

export default MembershipSection
